import { useLocation } from "react-router-dom";

import Stack from "@mui/material/Stack";

import { IgChip } from "@intelligems/ig-theme";

import { useGetOrg } from "@src/api/organizations/get-organization";
import { useExecutionPanelContext } from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import { resolveAgenticLogicView } from "@src/features/ai/pages/agentic-logic/surface/surface";
import { useActiveAgenticLogicSurface } from "@src/features/ai/pages/agentic-logic/surface/useActiveAgenticLogicSurface";
import { useSelector } from "@src/store";
import { getIsNewSession } from "@src/store/slices/aiChat";
import { selectAgenticLogicRuleIdeas } from "@src/store/slices/appSettings";

/**
 * The rule ideas Atlas generated for this store, offered as a scrollable row of
 * chips directly above the rail's composer. Picking one sends it as the first
 * message.
 *
 * Offered on ONE surface and view: the embedded rule builder's creation view —
 * the "add a rule" flow taken over by an upsell's settings dialog, where the
 * author arrived with a placement in mind and no rule yet, and the ideas are
 * written for that placement's serving surface. The standalone
 * surfaces have their own creation view, which lists these same ideas as its
 * page content, and editing an existing rule is never a cold start: an idea
 * there would offer to build a different rule inside the rule on screen.
 *
 * A cold-start affordance only: once the conversation has turns, an idea would
 * offer to start a different rule inside a session that is already about one,
 * so the row disappears. It also steps aside for a parked card ask, which owns
 * the space above the composer while it is on screen.
 *
 * The ideas are read from the store the creation view already fills — this
 * never generates them, because generating is a workflow run and two owners
 * would run it twice.
 */
export const AtlasSuggestionChips = () => {
  const orgId = useGetOrg().data?.org?.id;
  const isNewSession = useSelector(getIsNewSession);
  const { inputRequirement, submitInput, disabled, streaming } =
    useExecutionPanelContext();
  // This row renders inside the rail, which lives outside the page tree, so
  // the surface comes from the registry. An embedded surface namespaces its
  // view param away from the host's own query string.
  const surface = useActiveAgenticLogicSurface();
  // The same set the creation view holds: ideas are cached per placement
  // surface, so reading them without the surface would show the row a
  // different set from the one the page beside it generated.
  const ideas = useSelector(
    selectAgenticLogicRuleIdeas(orgId, surface?.placementSurface)
  );
  const view = resolveAgenticLogicView(
    useLocation().search,
    surface?.ruleParam
  );
  const onEmbeddedCreation =
    surface?.kind === "embedded" && view.kind === "new";

  const cardAskParked = inputRequirement.userInput.type !== "text";
  if (
    !onEmbeddedCreation ||
    ideas.length === 0 ||
    !isNewSession ||
    cardAskParked ||
    disabled ||
    streaming
  ) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      role="list"
      aria-label="Rule ideas for your store"
      sx={{
        flexWrap: "nowrap",
        overflowX: "auto",
        // The scroller spans the composer's own width, so a chip that runs off
        // the end is scrolled to rather than clipped by the rail's edge.
        minWidth: 0,
        maxWidth: "100%",
        px: 1,
        pb: 0.5,
        // The row scrolls rather than wrapping: the rail is narrow and a
        // wrapped row would push the composer down as ideas grow.
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {ideas.map((idea) => (
        <IgChip
          key={idea}
          role="listitem"
          label={idea}
          clickable
          onClick={() => submitInput({ type: "text", content: idea })}
          sx={{ flexShrink: 0, maxWidth: 240 }}
        />
      ))}
    </Stack>
  );
};
